import { ImmSearchParams } from '$lib/utils/url/ImmSearchParams';

export class ImmURL {
    private readonly internal: URL;

    constructor(init: URL | ImmURL | string) {
        if (init instanceof URL) {
            this.internal = init;
        } else if (init instanceof ImmURL) {
            this.internal = new URL(init.toString());
        } else {
            this.internal = new URL(init);
        }
    }

    clone(cb: (s: URL) => void): ImmURL {
        const newURL = new URL(this.internal.toString());
        cb(newURL);
        return new ImmURL(newURL);
    }

    withHash(v: string): ImmURL {
        return this.clone((url) => {
            url.hash = v;
        });
    }

    withHost(v: string): ImmURL {
        return this.clone((url) => {
            url.host = v;
        });
    }

    withHostname(v: string): ImmURL {
        return this.clone((url) => {
            url.hostname = v;
        });
    }

    withHref(v: string): ImmURL {
        return this.clone((url) => {
            url.href = v;
        });
    }

    withPassword(v: string): ImmURL {
        return this.clone((url) => {
            url.password = v;
        });
    }

    withPathname(v: string): ImmURL {
        return this.clone((url) => {
            url.pathname = v;
        });
    }

    withPort(v: string): ImmURL {
        return this.clone((url) => {
            url.port = v;
        });
    }

    withProtocol(v: string): ImmURL {
        return this.clone((url) => {
            url.protocol = v;
        });
    }

    withUsername(v: string): ImmURL {
        return this.clone((url) => {
            url.username = v;
        });
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/hash) */
    get hash(): string {
        return this.internal.hash;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/host) */
    get host(): string {
        return this.internal.host;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/hostname) */
    get hostname(): string {
        return this.internal.hostname;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/href) */
    get href(): string {
        return this.internal.href;
    }

    toString(): string {
        return this.internal.toString();
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/origin) */
    get origin(): string {
        return this.internal.origin;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/password) */
    get password(): string {
        return this.internal.password;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/pathname) */
    get pathname(): string {
        return this.internal.pathname;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/port) */
    get port(): string {
        return this.internal.port;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/protocol) */
    get protocol(): string {
        return this.internal.protocol;
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/search) */
    get search(): string {
        return this.internal.search;
    }

    get searchParams(): ImmSearchParams {
        return new ImmSearchParams(this.internal.searchParams);
    }

    withSearchParams(params: ImmSearchParams | URLSearchParams): ImmURL {
        return this.clone((v) => {
            v.search = new ImmSearchParams(params).toString();
        });
    }

    mapSearchParams(cb: (params: ImmSearchParams) => ImmSearchParams) {
        return this.withSearchParams(cb(this.searchParams));
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/URL/username) */
    get username(): string {
        return this.internal.username;
    }

    toStringWithoutProtocol(this: this): string {
        const s = this.withProtocol('https:').toString();
        return s.replace(/^https:\/\//, '');
    }
}
